import { useState } from 'react';
import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/inputs';
import { BANSHEE_TERMS, BANSHEE_BADGES, BANSHEE_COMPARE_PATH, BANSHEE_PATH, MAX_CONNECTIONS, PLANS, money } from '@/shared/pricing';
import { cn } from '@/lib/utils';
import { useSeo } from '@/shared/seo';
import { useFetch } from '@/hooks/useFetch';
import { loadProducts } from '@/shared/catalog';
import { builtInState } from '@/shared/products';

export const route = { path: '/banshee-7x2q9k', layout: 'public', access: 'public' };

const LABEL = 'text-[11px] uppercase tracking-[0.22em] text-primary';
const GLOW = 'bg-linear-to-r from-primary to-highlight bg-clip-text text-transparent';

export default function Banshee() {
  const [conns, setConns] = useState(1);
  const [term, setTerm] = useState('1y');
  const t = BANSHEE_TERMS.find((x) => x.id === term);
  const price = t.prices[conns - 1];
  // Paused from Admin > Products stops new Banshee orders here too.
  const { data: products } = useFetch(loadProducts, []);
  const onSale = builtInState(products, 'Banshee').onSale;

  // Invitation-only: kept out of search, and deliberately not listed in robots.txt.
  useSeo({ title: 'Private packages', noindex: true });

  const features = [
    `${conns} simultaneous screen${conns > 1 ? 's' : ''}`,
    'Full Ghost Cable channel lineup',
    'On-demand vault',
    'All supported devices',
  ];

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-16 md:px-10 md:pt-24">
      <p className={cn(LABEL, 'flex items-center gap-2')}>
        <ApperIcon name="Lock" size={12} />
        Private packages · invitation only
      </p>
      <h1 className={cn('mt-4 font-heading text-[clamp(84px,14vw,180px)] leading-[0.9]', GLOW)}>
        {PLANS.Banshee.name}
      </h1>
      <p className="mt-5 text-xl text-foreground/85">{PLANS.Banshee.tagline}</p>
      <p className="mt-3 max-w-lg text-[15px] leading-7 text-muted-foreground">{PLANS.Banshee.blurb}</p>

      <div className="mt-16 grid gap-14 md:grid-cols-[1.15fr_1fr] md:gap-20">
        <div className="border-t border-border pt-10">
          <p className={LABEL}>Connections</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {Array.from({ length: MAX_CONNECTIONS }, (_, i) => i + 1).map((n) => (
              <Pill key={n} on={conns === n} onClick={() => setConns(n)}>
                {n}
              </Pill>
            ))}
          </div>
          <p className={cn(LABEL, 'mt-10')}>Subscription package</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {BANSHEE_TERMS.map((x) => (
              <Pill key={x.id} on={term === x.id} onClick={() => setTerm(x.id)}>
                {x.label}
              </Pill>
            ))}
          </div>
        </div>

        <div className="border-t border-border pt-10">
          <p className={LABEL}>Your package</p>
          <div className="mt-6 flex flex-wrap items-end gap-4">
            <span className={cn('text-7xl font-light leading-none', GLOW)}>{money(price)}</span>
            <span className="pb-2 text-sm text-muted-foreground">
              USD · {t.label}
              <br />≈ {money(Math.round((price / t.months) * 100) / 100)}/mo
            </span>
          </div>
          {term === '1y' && (
            <span className="mt-4 inline-block rounded-full border border-highlight/40 bg-highlight/15 px-3 py-1 text-xs text-highlight">
              {BANSHEE_BADGES[conns - 1]}
            </span>
          )}
          <ul className="mt-8 space-y-2.5 text-[15px] text-foreground/80">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <ApperIcon name="Check" size={16} className="text-primary" />
                {f}
              </li>
            ))}
          </ul>
          {onSale ? (
            <>
              <Button size="lg" className="mt-10" asChild>
                <Link to={`/checkout?plan=Banshee&term=${term}&connections=${conns}`}>Summon the Banshee</Link>
              </Button>
              <p className="mt-4 text-xs text-muted-foreground">
                Banshee packages are paid by card, Wise, crypto or e-Transfer.
              </p>
            </>
          ) : (
            <>
              <Button size="lg" className="mt-10" disabled>
                Paused for now
              </Button>
              <p className="mt-4 max-w-sm text-xs text-muted-foreground">
                New Banshee packages are paused for the moment. Message us and we will let you know when they open again.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mt-20 border-t border-border pt-10">
        <p className={LABEL}>Every package</p>
        <div className="mt-6 overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Term</th>
                {Array.from({ length: MAX_CONNECTIONS }, (_, i) => (
                  <th key={i} className="px-4 py-3 text-right font-medium">
                    {i + 1} conn.
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BANSHEE_TERMS.map((row) => (
                <tr key={row.id} className="border-t border-border">
                  <td className="px-4 py-3">{row.label}</td>
                  {row.prices.map((p, i) => {
                    const on = row.id === term && i === conns - 1;
                    return (
                      <td key={`${row.id}-${i}`} className="px-2 py-2 text-right align-top">
                        <button
                          type="button"
                          onClick={() => {
                            setTerm(row.id);
                            setConns(i + 1);
                          }}
                          aria-pressed={on}
                          className={cn(
                            'w-full rounded-md px-2 py-1.5 text-right transition-colors',
                            on ? 'bg-primary text-primary-foreground' : 'hover:bg-accent hover:text-accent-foreground',
                          )}
                        >
                          {money(p)}
                          {row.id === '1y' && (
                            <span className={cn('block text-[10px]', on ? 'text-primary-foreground/80' : 'text-highlight')}>
                              {BANSHEE_BADGES[i]}
                            </span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Link
          to={BANSHEE_COMPARE_PATH}
          className="mt-8 inline-flex items-center gap-2 text-sm text-primary underline-offset-4 hover:underline"
        >
          Compare {PLANS.Banshee.name} with {PLANS.Wraith.name} and {PLANS.Demon.name}
          <ApperIcon name="ArrowRight" size={14} />
        </Link>
        <p className="mt-6 text-xs text-muted-foreground">
          This page is private. Please don't share the link publicly.
          <span className="sr-only">{BANSHEE_PATH}</span>
        </p>
      </div>
    </div>
  );
}
